import React, { useState, useEffect } from "react"
import { Checkbox, Loader, ScrollArea } from "@mantine/core"
import { Button } from "@quorumus/plum"

import classes from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/SavedQuestionsModal.module.css"
import { QuestionModal } from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/QuestionModal"
import { PERSONALIZED_PAGES_KEY } from "@/pages/campaign-form/components/campaigns/personalizedMessages/Messaging"
import { useSavedQuestions } from "@/services/campaigns"
import { useFormContext } from "react-hook-form"
import { CustomField } from "@/types/djangio"
import { Icon } from "@/components"

const { TagType } = DjangIO.app.person.types

const namesByTagTypes = {
    [TagType.string.value]: "Text Field",
    [TagType.number.value]: "Number",
    [TagType.date.value]: "Date",
    [TagType.boolean.value]: "Yes/No",
    [TagType.single_option_list.value]: "Single selection",
    [TagType.multi_options_list.value]: "Multiple selection",
}

interface Props {
    opened: boolean
    onClose: () => void
    addQuestion: () => void
}

const CONTENT_HEIGHT = 352

export const SavedQuestionsModal = (props: Props) => {
    const formContext = useFormContext()
    const personalizedPages = formContext.getValues(PERSONALIZED_PAGES_KEY) as Record<string, CustomField[]>
    const attachedQuestionsIds = Object.values(personalizedPages)[0]?.map((question) => question.id) ?? []
    const [checkedQuestions, setCheckedQuestions] = useState([...(Object.values(personalizedPages)[0] ?? [])])
    const savedQuestions = useSavedQuestions()

    const isLoading = savedQuestions.isLoading && !Array.isArray(savedQuestions.data)
    const hasSavedQuestions = !isLoading && savedQuestions.data.length > 0

    useEffect(() => {
        setCheckedQuestions([...(Object.values(personalizedPages)[0] ?? [])])
    }, [personalizedPages])

    const handleSubmit = () => {
        formContext.setValue(PERSONALIZED_PAGES_KEY, { 0: [...checkedQuestions] })

        props.onClose()
    }

    const handleOnClose = () => {
        setCheckedQuestions([...(Object.values(personalizedPages)[0] ?? [])])
        props.onClose()
    }

    return (
        <QuestionModal
            title="Saved system questions"
            size="lg"
            opened={props.opened}
            onClose={handleOnClose}
            footer={
                <div className={classes.footer}>
                    <Button variant="secondary" onClick={handleOnClose}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit}>Save</Button>
                </div>
            }
        >
            <ScrollArea offsetScrollbars h={CONTENT_HEIGHT} type="auto">
                <div className={classes.listOfQuestions}>
                    {isLoading ? (
                        <Loader size="sm" />
                    ) : hasSavedQuestions ? (
                        savedQuestions.data?.map((question: CustomField) => (
                            <QuestionItem
                                key={question.id}
                                question={question}
                                isChecked={attachedQuestionsIds.includes(question.id)}
                                onToggle={setCheckedQuestions}
                            />
                        ))
                    ) : (
                        <EmptyState onAddQuestion={props.addQuestion} onClose={handleOnClose} />
                    )}
                </div>
            </ScrollArea>
        </QuestionModal>
    )
}

const QuestionItem = ({ question, isChecked, onToggle }) => (
    <div className={classes.question} key={question.id}>
        <Checkbox
            key={question.id}
            label={
                <div className={classes.label}>
                    <p className={classes.checkbox}>{question.name}</p>
                    <span className={classes.tagType}>{namesByTagTypes[question.tag_type]}</span>
                </div>
            }
            onChange={(e) => {
                if (e.target.checked) {
                    onToggle((prevState) => [...prevState, question])
                    return
                }

                onToggle((prevState) => prevState.filter((checkedQuestion) => checkedQuestion.id !== question.id))
            }}
            defaultChecked={isChecked}
        />
        <span className={classes.placeholder}>{`{{${question.slug}}}`}</span>
    </div>
)

const EmptyState = ({ onAddQuestion, onClose }) => (
    <div className={classes.emptyState}>
        <Icon icon="tasks" className={classes.icon} />
        <div className={classes.textBlock}>
            <p className={classes.title}>No questions found</p>
            <p className={classes.description}>You don't have any questions saved. Add a question to get started.</p>
        </div>
        <Button
            variant="primary"
            onClick={() => {
                onAddQuestion()
                onClose()
            }}
        >
            <Icon icon="plus" />
            Add question
        </Button>
    </div>
)
